import { useParams, useLocation, useNavigate, Navigate } from "react-router";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
  DialogDescription,
} from "@radix-ui/react-dialog";
import Header from "@/features/layout/components/Header";
import { useActividadAprendiz } from "@/hooks/actividadAprendiz/useActividadAprendiz";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import {
  useEvaluacionStreaming,
  extractRetroContent,
} from "@/features/evaluacion";
import type { ChatBoxRef } from "@/features/evaluacion/components/ChatBox";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import Footer from "@/features/layout/components/Footer";
import { useActividadCompleta } from "@/hooks/useActividadCompleta";
import { toast } from "sonner";
import { ActividadUploadView } from "./ActividadUploadView";
import { ActividadFeedbackView } from "./ActividadFeedbackView";
import { getIntentosInfo } from "@/shared/utils/intentos";

export default function Actividad() {
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation();
  const { usuarioInfo } = useCurrentUser();
  const navigate = useNavigate();

  // State como fuente primaria (navegación normal)
  const stateActividad = state?.actividad;
  const stateCerebro = state?.cerebro;
  const forceInitialView = state?.forceInitialView;

  // Fetch como fallback (recarga de página — sin state)
  const needsFetch = !stateActividad;
  const actividadId = stateActividad?.id || Number(id);
  const {
    data: actividadCompletaData,
    isLoading: isLoadingCompleta,
    isError: isErrorCompleta,
  } = useActividadCompleta(actividadId, usuarioInfo?.aprendiz?.id, needsFetch);

  // Derivar datos: state tiene prioridad, fetch como fallback
  const actividad = stateActividad ?? actividadCompletaData?.actividad;
  const cerebro = stateCerebro ?? actividadCompletaData?.cerebro ?? "";

  const [showRetroView, setShowRetroView] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const isInRetroViewRef = useRef(true);
  const { data: actividadAprendiz } = useActividadAprendiz(
    actividad?.id || Number(id),
    usuarioInfo?.aprendiz?.id,
  );

  // Streaming de evaluación
  const {
    evaluarConStreaming,
    feedback,
    retroContent: streamingRetroContent,
    isStreaming,
    sessionId: streamingSessionId,
    error,
  } = useEvaluacionStreaming({
    context: {
      actividadId: actividad?.id || Number(id),
      aprendizId: usuarioInfo?.aprendiz?.id ?? 0,
      actividadAprendizId: actividadAprendiz?.id,
      cerebro,
      archivoLimpioUrl: actividad?.archivoLimpio?.url || "",
      archivoLimpioKey: actividad?.archivoLimpio?.llave || "",
      programa: actividadAprendiz?.actividadPrograma?.idPrograma ?? 0,
      limiteIntentos: getIntentosInfo({
        intentosExtras: actividadAprendiz?.intentosExtras,
        limiteIntentos: actividadAprendiz?.actividadPrograma?.programa?.limiteIntentos,
      }).total,
    },
    onFirstChunk: () => {
      setIsRetroReady(true);
      isPendingTransitionRef.current = true;
      if (isInRetroViewRef.current) {
        setTimeout(() => {
          setIsAnimating(true);
          setTimeout(() => {
            setShowRetroView(false);
            isInRetroViewRef.current = false;
            setIsRetroReady(false);
            isPendingTransitionRef.current = false;
            setTimeout(() => setIsAnimating(false), 50);
          }, 300);
        }, 2000);
      }
    },
    onComplete: (newSessionId) => {
      if (!newSessionId) {
        console.error("Evaluación completada, pero sin session ID");
      }
    },
  });

  // Estado compartido del flujo
  const [retroContent, setRetroContent] = useState<string>("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isRetroReady, setIsRetroReady] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [stepperError, setStepperError] = useState(false);
  const [preguntasUsadas, setPreguntasUsadas] = useState<Set<string>>(new Set());

  const retroRef = useRef<HTMLDivElement>(null);
  const chatBoxRef = useRef<ChatBoxRef>(null);
  const isPendingTransitionRef = useRef(false);
  const forceInitialViewProcessedRef = useRef(false);

  // ── Effects ──
  useEffect(() => {
    if (error) {
      setErrorModalOpen(true);
      setStepperError(true);
    }
  }, [error]);

  useEffect(() => {
    if (!actividadAprendiz) return;
    if (forceInitialView && !forceInitialViewProcessedRef.current) {
      setShowRetroView(true);
      isInRetroViewRef.current = true;
      setIsAnimating(false);
      setRetroContent("");
      setSessionId(null);
      forceInitialViewProcessedRef.current = true;
      return;
    }
    if (actividadAprendiz?.ultimoIntento?.retro) {
      const prevRetro = extractRetroContent(actividadAprendiz.ultimoIntento.retro);
      if (prevRetro) {
        setRetroContent(prevRetro);
        setSessionId(actividadAprendiz.ultimoIntento.sessionId ?? null);
        if (!isPendingTransitionRef.current) {
          setShowRetroView(false);
          isInRetroViewRef.current = false;
          setIsAnimating(false);
        }
      }
    } else {
      setShowRetroView(true);
      isInRetroViewRef.current = true;
    }
    if (
      actividadAprendiz?.ultimoIntento?.preguntasUsadas &&
      Array.isArray(actividadAprendiz.ultimoIntento.preguntasUsadas)
    ) {
      setPreguntasUsadas(new Set(actividadAprendiz.ultimoIntento.preguntasUsadas));
    }
  }, [actividadAprendiz, forceInitialView]);

  useEffect(() => {
    if (streamingRetroContent) setRetroContent(streamingRetroContent);
  }, [streamingRetroContent]);

  useEffect(() => {
    if (streamingSessionId) setSessionId(streamingSessionId);
  }, [streamingSessionId]);

  useEffect(() => {
    if (retroRef.current && isStreaming) {
      retroRef.current.scrollTop = retroRef.current.scrollHeight;
    }
  }, [retroContent, isStreaming]);

  // ── Handlers ──
  const handleFileUpload = async (file: File) => {
    setStepperError(false);
    await evaluarConStreaming(file);
  };

  const handleVolverAIntentar = () => {
    setIsAnimating(true);
    setIsRetroReady(false);
    setTimeout(() => {
      setShowRetroView(true);
      isInRetroViewRef.current = true;
      setRetroContent("");
      setSessionId(null);
      setTimeout(() => setIsAnimating(false), 50);
    }, 300);
  };

  const handleBack = () => {
    if (state && state.fromInstrucciones) {
      navigate("/instrucciones-actividad", { state });
    } else {
      navigate("/programas");
    }
  };

  // ── Guards ──
  const isLoadingData = needsFetch && isLoadingCompleta;
  const shouldRedirect =
    !isLoadingData &&
    (!actividad || (needsFetch && (isErrorCompleta || actividadCompletaData === null)));

  useEffect(() => {
    if (shouldRedirect) {
      toast.error("No se encontró la actividad o no está asignada.");
    }
  }, [shouldRedirect]);

  if (isLoadingData) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center h-screen bg-white">
          <Loader2 className="animate-spin text-[var(--primary)]" size={32} />
        </div>
      </>
    );
  }

  if (shouldRedirect) {
    return <Navigate to="/programas" replace />;
  }

  return (
    <div className="h-dvh flex flex-col overflow-hidden">
      {/* Modal de error */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 z-[9999] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogContent
            className="fixed left-1/2 top-1/2 z-[10000] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl bg-[var(--card-background)] p-6 shadow-xl"
            style={{ outline: "none" }}
          >
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertCircle className="text-red-600 dark:text-red-400" size={28} />
              </div>
              <DialogTitle className="text-lg font-semibold text-[var(--foreground)]">
                Ocurrió un error
              </DialogTitle>
              <DialogDescription className="text-sm text-[var(--foreground)] opacity-70">
                {error}
              </DialogDescription>
              <Button onClick={() => setErrorModalOpen(false)} className="w-full mt-2">
                Entendido
              </Button>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <Header />
      <main
        className={`flex-1 flex flex-col min-h-0 ${
          showRetroView
            ? "w-full max-w-4xl mx-auto px-4 py-6 md:py-10 overflow-y-auto"
            : "w-full overflow-hidden"
        }`}
      >
        {showRetroView ? (
          <ActividadUploadView
            actividad={actividad}
            cerebro={cerebro}
            aprendizId={usuarioInfo?.aprendiz?.id ?? 0}
            actividadAprendiz={actividadAprendiz}
            onFileUpload={handleFileUpload}
            isStreaming={isStreaming}
            isRetroReady={isRetroReady}
            feedback={feedback}
            stepperError={stepperError}
            isAnimating={isAnimating}
            onBack={handleBack}
          />
        ) : (
          <ActividadFeedbackView
            actividad={actividad}
            cerebro={cerebro}
            retroContent={retroContent}
            sessionId={sessionId}
            isStreaming={isStreaming}
            actividadAprendiz={actividadAprendiz}
            preguntasUsadas={preguntasUsadas}
            onPreguntaUsada={setPreguntasUsadas}
            onVolverAIntentar={handleVolverAIntentar}
            onBack={handleBack}
            isAnimating={isAnimating}
            retroRef={retroRef}
            chatBoxRef={chatBoxRef}
          />
        )}
      </main>
      {showRetroView && <Footer />}
    </div>
  );
}
